// PRD.314 — the generic current-entry editor for any History submodule.
//
// EXTRACTED from PRD.310's `MedicalHistoryField` rather than copied: Medical
// History and every Specialty History now share this one implementation, so the
// autosave lifecycle, the stale-write guards and the voice pipeline exist once.
// Only the WRITE differs, and that arrives as an injected `onSave`.
//
// Lifecycle preserved exactly as PRD.310 shipped it:
//   - 600 ms debounced autosave
//   - blur flush
//   - unmount flush
//   - identity-captured writes (a resolving save lands on the row it was typed
//     into, never on whatever is active by then)
//   - failed writes restore the baseline so the next edit retries
//   - dictation APPENDS, never replaces

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { filterCatalogSuggestions } from '@helios/data-access';
import { MicButton } from '../../../hooks/MicButton';
import { useVoiceToText } from '../../../hooks/useVoiceToText';
import { getFindingTextareaRows } from '@helios/data-access'; // PRD.316
import { insertMedicalHistoryTranscript, needsSave } from './medical-history-logic';

/**
 * Floor for the editable field. Keeps a usable typing area when the column has
 * no spare height to give, and stops `flex: 1` collapsing it in a tight row.
 */
const MIN_EDITOR_HEIGHT = 140;

/** Quiet period before a keystroke run is persisted (matches InvoicesTab). */
const AUTOSAVE_DEBOUNCE_MS = 600;

export interface HistoryEntryEditorProps {
  /**
   * Identity of the record being edited. For Medical History this is the
   * encounter id; for Specialty History it is encounter id + type. A change
   * re-seeds the editor and resets the save baseline, and every in-flight save
   * is bound to the value captured when it started — so switching encounter,
   * patient or specialty type can never write stale text into the new target.
   */
  identity: string;
  /** Stored value for `identity`, as loaded. */
  storedValue: string;
  /** Persist `text` for `identity`. Throwing triggers the retry path. */
  onSave: (text: string, identity: string) => Promise<void>;
  /** Accessible label + placeholder for the field. */
  label: string;
  placeholder: string;
  /** Clinic setting — gates the CONTROLS only, never the stored text. */
  voiceEnabled: boolean;
  rows?: number;
  /**
   * PRD.316 compact mode. When set, the field starts at ONE row and grows only
   * with EXPLICIT newlines, up to this many rows, then scrolls internally.
   *
   * Additive: History sections omit it and keep their fixed-height,
   * flex-filling textarea exactly as before. Compact mode also drops the
   * `flex: 1` / `minHeight` growth, which exists so a history editor can absorb
   * a tall column — the opposite of what a dense findings list wants.
   */
  autoRowsMax?: number;
  /**
   * Optional catalogue suggestions shown beneath the field as the user types.
   *
   * PURELY ADDITIVE. The field stays a free-text editor: a suggestion only
   * fills the box, and text that matches nothing saves exactly as before. The
   * autosave path and voice dictation are untouched — a picked suggestion goes
   * through the same `setValue` + save the keyboard uses, so dictated text is
   * never gated on a catalogue match.
   *
   * Omitted by every existing caller, which therefore behaves identically.
   */
  suggestions?: readonly string[];
  /** Notifies the container so it can re-query as the user types. */
  onSearchChange?: (text: string) => void;
}

export function HistoryEntryEditor({
  identity,
  storedValue,
  onSave,
  label,
  placeholder,
  voiceEnabled,
  rows = 6,
  autoRowsMax,
  suggestions,
  onSearchChange,
}: HistoryEntryEditorProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';

  const [value, setValue] = useState(storedValue);
  // Suggestions open on focus and close on blur/pick. Kept local so no existing
  // state or effect changes shape for callers that pass none.
  const [suggestOpen, setSuggestOpen] = useState(false);
  const compact = autoRowsMax !== undefined;

  // Matching rules live in data-access so they are unit-tested rather than
  // buried in a component (see `filterCatalogSuggestions`).
  const visibleSuggestions = useMemo(
    () => (suggestions?.length ? filterCatalogSuggestions(suggestions, value) : []),
    [suggestions, value],
  );
  const valueRef = useRef(storedValue);
  const lastSavedRef = useRef(storedValue);

  // Re-seed when the edited record changes. Remounting is not guaranteed when
  // the workspace swaps encounters in place, so encounter B must never inherit
  // encounter A's text — and dental must never inherit derma's.
  const identityRef = useRef(identity);
  useEffect(() => {
    if (identityRef.current === identity) return;
    identityRef.current = identity;
    valueRef.current = storedValue;
    lastSavedRef.current = storedValue;
    setValue(storedValue);
  }, [identity, storedValue]);

  // Adopt a value that arrives after mount (the row synced late) — but only
  // while the field is still untouched, so a sync round-trip never clobbers
  // what the clinician is typing.
  useEffect(() => {
    if (identityRef.current !== identity) return;
    if (valueRef.current !== '' || storedValue === '') return;
    valueRef.current = storedValue;
    lastSavedRef.current = storedValue;
    setValue(storedValue);
  }, [identity, storedValue]);

  const save = useCallback(
    async (text: string) => {
      if (!needsSave(text, lastSavedRef.current)) return;
      const targetIdentity = identityRef.current;
      const previousBaseline = lastSavedRef.current;
      lastSavedRef.current = text;
      try {
        await onSave(text, targetIdentity);
      } catch (err) {
        lastSavedRef.current = previousBaseline;
        console.error('[history-entry] autosave failed', err);
      }
    },
    [onSave],
  );

  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  });

  // Debounced autosave. On-blur alone is unreliable — a clinician who types and
  // then navigates (or closes the tab) without blurring would lose the edit.
  useEffect(() => {
    const t = setTimeout(() => {
      void saveRef.current(valueRef.current);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value]);

  // Final flush on unmount, so a pending debounce cancelled by teardown still
  // reaches the record it belongs to.
  useEffect(() => {
    return () => {
      void saveRef.current(valueRef.current);
    };
  }, []);

  // ── Voice-to-text ──
  //
  // The shared Visit Note pipeline: `useVoiceToText` (MediaRecorder, mic
  // permission, MIME negotiation, 5-minute cap, timers, stream cleanup) over
  // `transcribeAudioBlob`, plus the shared `MicButton`. Plain `transcription`
  // mode with no summaryStyle — the words as spoken, with no summarization,
  // translation, polishing or structured extraction. The clinic switch gates
  // AVAILABILITY only: previously dictated text stays ordinary editable
  // narrative that autosaves exactly like typed text.
  const vttEnabled = voiceEnabled && Boolean(import.meta.env.VITE_VOICE_TO_TEXT_URL);
  const vtt = useVoiceToText({
    mode: 'transcription',
    onResult: (text) => {
      // Append, never replace, and only into THIS entry — historical blocks are
      // rendered separately and are never touched.
      const next = insertMedicalHistoryTranscript(valueRef.current, text);
      if (next === valueRef.current) return;
      valueRef.current = next;
      setValue(next);
      void saveRef.current(next);
    },
  });

  return (
    // `flex: 1` so the editor absorbs whatever height the surrounding column
    // has spare. Outside a flex parent (tabs / accordion) it is inert and the
    // field keeps its natural `rows` height.
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        flex: 1,
        minHeight: 0,
      }}
    >
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          flex: compact ? undefined : 1,
        }}
      >
        <textarea
          aria-label={label}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            valueRef.current = e.target.value;
            setValue(e.target.value);
            if (suggestions) {
              setSuggestOpen(true);
              onSearchChange?.(e.target.value);
            }
          }}
          onFocus={() => {
            // Open on focus so the catalogue is browsable without having to
            // guess a first letter.
            if (suggestions) setSuggestOpen(true);
          }}
          onBlur={() => {
            void saveRef.current(valueRef.current);
            // Deferred so a mousedown on a suggestion still lands.
            setTimeout(() => setSuggestOpen(false), 120);
          }}
          rows={compact ? getFindingTextareaRows(value, autoRowsMax) : rows}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            // Grow into the available height, but never below the natural
            // `rows` size — a short column must still give a usable field.
            // Compact mode opts out: its height IS the row count.
            flex: compact ? undefined : 1,
            minHeight: compact ? undefined : MIN_EDITOR_HEIGHT,
            // Past the row cap the field scrolls instead of pushing the list
            // down; manual resize would fight the newline-derived height.
            resize: compact ? 'none' : 'vertical',
            overflowY: compact ? 'auto' : undefined,
            padding: compact ? '6px 8px' : '8px 10px',
            paddingRight: vttEnabled ? (compact ? 38 : 44) : compact ? 8 : 10,
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${vtt.status === 'recording' ? primary : 'var(--border)'}`,
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text)',
            fontFamily: 'inherit',
            outline: 'none',
            lineHeight: 'var(--line-height-body)',
          }}
        />
        {vttEnabled && (
          <MicButton
            // Compact rows are ~37px tall, so the 32px button's default
            // `top: 8` overflows the field. Centre it on the first line box
            // instead — the same line the tooth label and remove action align to.
            positionStyle={compact ? { top: 2, right: 4 } : undefined}
            status={vtt.status}
            primary={primary}
            elapsed={vtt.elapsed}
            maxDuration={vtt.maxDuration}
            onStart={() => vtt.start()}
            onStop={() => void vtt.stop()}
            onReset={() => vtt.reset()}
          />
        )}

        {/* Catalogue suggestions. Picking one only FILLS the field — the same
            save path as typing runs afterwards, so nothing about persistence
            is special-cased for catalogue values. */}
        {suggestOpen && visibleSuggestions.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 2,
              maxHeight: 200,
              overflowY: 'auto',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.08))',
              zIndex: 20,
            }}
          >
            {visibleSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                // `preventDefault` keeps focus in the textarea, so the blur
                // save does not race the pick.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  valueRef.current = s;
                  setValue(s);
                  setSuggestOpen(false);
                  onSearchChange?.('');
                  void saveRef.current(s);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 10px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text)',
                  cursor: 'pointer',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {vtt.status === 'transcribing' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>
            <NavIcon name="loader" />
          </span>
          Transcribing...
        </div>
      )}

      {/* Failure leaves the editor untouched. The mic doubles as Retry. */}
      {vtt.status === 'error' && vtt.error && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: '#dc2626' }}>
          {vtt.error} — tap the microphone to try again.
        </div>
      )}
    </div>
  );
}
